/**
 * The game screen's HUD, v2 (docs/ux.md §2, §5, §7, §13; spec v2): a slim status bar and the
 * tools at the top, the turn's one round button in the corner (the selected unit has its own
 * card, ShipInspector.tsx), and small transient things (the shared-cell picker, toasts). The squadron is chosen
 * on the map itself (U-MUST-01): the HUD only counts it, by the wind button.
 * Buttons show icons only; names, shortcuts and reasons are in their tooltips (HudButton).
 * Everything reads the turn machine and asks the rules engine; nothing here keeps game state.
 */
import { getPlayerColour } from '@brig/nations';
import type { GameState, Side } from '@brig/rules-engine';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useGameExit } from './exit.ts';
import { NationFlag } from '../nations/NationFlag.tsx';
import { ShipToken } from '../ships/ShipToken.tsx';
import { dockTurnsLeft, fortRepairTurnsLeft, royalHold, tokenStateOf } from '../ships/shipState.ts';
import { attackersOf, shotsLeft } from './fire.ts';
import { primaryLink } from './Game.css.ts';
import { current, undoDepth } from './history.ts';
import * as css from './Hud.css.ts';
import { HudButton } from './HudButton.tsx';
import { Icon, type IconName } from './icons.tsx';
import { unitLabel } from './names.ts';
import { uiPhase } from './phase.ts';
import { squadronCandidates } from './turn.ts';
import { promptKey } from './prompt.ts';
import { WindArrow } from './TurnControls.tsx';
import { lastTurnRange, liveSquadron } from './turnMachine.ts';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';

export { HudButton, TooltipLayer } from './HudButton.tsx';

interface Props {
  readonly snapshot: TurnSnapshot;
  readonly send: TurnSend;
}

const other = (side: Side): Side => (side === 'red' ? 'blue' : 'red');

/** U-TURN-04: a side as the board shows it, its flag and its player colour. */
function SideName({ snapshot, side, children }: { snapshot: TurnSnapshot; side: Side; children: ReactNode }) {
  const { nations, colours } = snapshot.context.history;
  return (
    <span className={css.side} style={{ ['--side-colour' as string]: getPlayerColour(colours[side]).hex }}>
      <NationFlag nation={nations[side]} height={14} decorative />
      {children}
    </span>
  );
}

/** What a HUD button opens over the board; `newGame` is the sandbox's setup of the next game. */
export type Panel = 'fleet' | 'log' | 'menu' | 'settings' | 'help' | 'learn' | 'newGame';

/**
 * The top-left group (spec v2 §7): the menu, the turn, the side to act, ships and forts, and
 * the weather — only once the wind is known; before that, the phase. The prompt line follows.
 */
export function HudStatus({
  snapshot,
  panel,
  onPanel,
}: {
  snapshot: TurnSnapshot;
  panel: Panel | null;
  onPanel: (p: Panel | null) => void;
}) {
  const { t } = useTranslation();
  const exitTo = useGameExit();
  const state = current(snapshot.context.history);
  const phase = uiPhase(snapshot);
  const side = state.turn.side;
  const name = state.players[side].name;
  const spin = state.turn.spin;
  const afloat = (s: Side) => state.ships.filter((x) => x.side === s && !x.sunk).length;
  const total = (s: Side) => state.ships.filter((x) => x.side === s).length;
  const forts = state.forts.filter((f) => f.side === side);
  const standing = forts.filter((f) => !f.destroyed);
  const damaged = standing.filter((f) => f.hits > 0).length;
  const hold = state.holdCount[side];
  const shipsTitle = (s: Side) => t('game.hud.ships', { n: afloat(s), total: total(s) });
  const fortsTitle = t('game.hud.forts', { n: standing.length, total: forts.length, damaged });
  const windKnown = spin !== null && phase !== 'wind';
  const weather = windKnown
    ? `${t(`game.weather.${spin.weather}`)} · ${t(`game.dir.${spin.direction}`)}`
    : t(`game.phase.${phase}`);
  return (
    <>
      <div className={css.status} data-testid="turn-line" role="group" aria-label={t('game.hud.status')}>
        <HudButton
          icon="menu"
          label={t('game.hud.menu')}
          expanded={panel === 'menu'}
          onClick={() => {
            onPanel(panel === 'menu' ? null : 'menu');
          }}
        />
        <span className={css.turnNo}>{t('game.turn', { n: state.turn.number })}</span>
        {phase !== 'over' ? (
          <SideName snapshot={snapshot} side={side}>
            <span className={css.sideName}>{name}</span>
            <span className={css.srOnly}>{t('game.toAct', { side: name })}</span>
          </SideName>
        ) : null}
        <span className={css.rule} aria-hidden="true" />
        <span className={css.stat} data-tip={shipsTitle(side)} aria-label={shipsTitle(side)}>
          <Icon name="fleet" />
          {afloat(side)}
          <span className={css.statSoft}>/{total(side)}</span>
        </span>
        <span
          className={`${css.stat} ${css.wideStat}`}
          data-tip={shipsTitle(other(side))}
          aria-label={shipsTitle(other(side))}
        >
          <NationFlag nation={snapshot.context.history.nations[other(side)]} height={11} decorative />
          {afloat(other(side))}
          <span className={css.statSoft}>/{total(other(side))}</span>
        </span>
        {forts.length > 0 ? (
          <span className={`${css.stat} ${css.wideStat}`} data-tip={fortsTitle} aria-label={fortsTitle}>
            <Icon name="fort" />
            {standing.length}
            <span className={css.statSoft}>/{forts.length}</span>
            {damaged > 0 ? <span>•</span> : null}
          </span>
        ) : null}
        {hold > 0 ? (
          <span
            className={css.stat}
            data-tip={t('game.hud.royalHold', { n: hold, max: state.ruleset.royalPortHoldTurns })}
          >
            <Icon name="flag" />
            {hold}/{state.ruleset.royalPortHoldTurns}
          </span>
        ) : null}
        <span className={css.rule} aria-hidden="true" />
        <span
          className={css.weather}
          data-known={windKnown}
          data-testid={windKnown ? 'wind-badge' : undefined}
          data-tip={weather}
        >
          {windKnown ? (
            <>
              <Icon name="wind" />
              <WindArrow direction={spin.direction} size={14} />
            </>
          ) : null}
          <span className={css.weatherText}>{weather}</span>
        </span>
        <span className={css.srOnly}>{t(`game.phase.${phase}`)}</span>
      </div>
      <p className={css.prompt} aria-live="polite" data-testid="prompt">
        {t(promptKey(snapshot), { n: liveSquadron(snapshot.context).length, side: name })}
        {phase === 'over' ? (
          <>
            {' '}
            <Link to={exitTo} className={css.promptLink}>
              {t('game.over.leave')}
            </Link>
          </>
        ) : null}
      </p>
    </>
  );
}

/**
 * Fleet, log, settings and help at the top right (spec v2 §7); phones keep fleet and log.
 * During one's own turn the opponent's last turn can be watched again from here (U-LOG-05).
 */
export function HudTools({
  snapshot,
  send,
  panel,
  onPanel,
}: Props & { panel: Panel | null; onPanel: (p: Panel | null) => void }) {
  const { t } = useTranslation();
  const toggle = (p: Panel) => () => {
    onPanel(panel === p ? null : p);
  };
  const phase = uiPhase(snapshot);
  const history = snapshot.context.history;
  const range = phase === 'muster' || phase === 'battle' ? lastTurnRange(history) : null;
  const watched = range ? history.states[range.from] : undefined;
  return (
    <div className={css.tools} role="toolbar" aria-label={t('game.hud.tools')}>
      {watched ? (
        <HudButton
          icon="replay"
          label={t('game.replay.watch', { side: watched.players[watched.turn.side].name })}
          keyHint="R"
          testId="watch-last-turn"
          onClick={() => {
            onPanel(null);
            send({ type: 'WATCH' });
          }}
        />
      ) : null}
      <HudButton
        icon="fleet"
        label={t('game.hud.fleet')}
        expanded={panel === 'fleet'}
        onClick={toggle('fleet')}
      />
      <HudButton
        icon="log"
        label={t('game.hud.log')}
        keyHint="L"
        expanded={panel === 'log'}
        onClick={toggle('log')}
      />
      <HudButton
        icon="settings"
        label={t('game.hud.settings')}
        expanded={panel === 'settings'}
        className={css.wideTool}
        onClick={toggle('settings')}
      />
      <HudButton
        icon="help"
        label={t('game.hud.help')}
        keyHint="?"
        expanded={panel === 'help'}
        className={css.wideTool}
        onClick={toggle('help')}
      />
    </div>
  );
}

/**
 * The turn's one primary action as a round button at the bottom right (spec v2 §7): cast the
 * wind, or end the turn. Undo and the phase's small extras stand beside it: while the squadron
 * is chosen on the map (U-MUST-01), "all / none"; the wind button carries the count, chosen of
 * those that may sail, and with nobody chosen it becomes "skip sailing" (U-MUST-03). Once the game is over, the
 * corner holds the way back to the logbook instead (U-OVER-02).
 */
export function TurnCorner({ snapshot, send }: Props) {
  const { t } = useTranslation();
  const exitTo = useGameExit();
  const state = current(snapshot.context.history);
  const phase = uiPhase(snapshot);
  const playing = phase === 'muster' || phase === 'battle';
  if (phase === 'over') {
    return (
      <div className={css.corner}>
        <Link to={exitTo} className={primaryLink} data-testid="primary-action">
          <Icon name="anchor" />
          {t('game.over.leave')}
        </Link>
      </div>
    );
  }
  if (!playing) return null;
  const canUndo = undoDepth(snapshot.context.history) > 0;
  const squadron = liveSquadron(snapshot.context).length;
  const candidates = squadronCandidates(state).length;
  const all = candidates > 0 && squadron === candidates;
  return (
    <div className={css.corner} role="toolbar" aria-label={t(`game.phase.${phase}`)}>
      <div className={css.cornerSide}>
        {phase === 'battle' && state.turn.phase === 'open' ? (
          <HudButton
            icon="raise"
            tone="float"
            label={t('game.action.raiseSails')}
            onClick={() => {
              send({ type: 'RAISE_SAILS' });
            }}
          />
        ) : null}
        {phase === 'muster' && candidates > 0 ? (
          <HudButton
            icon={all ? 'selectNone' : 'selectAll'}
            tone="float"
            label={all ? t('game.action.clear') : t('game.action.allToSea')}
            keyHint="A"
            testId="squadron-all"
            onClick={() => {
              send({ type: all ? 'SQUADRON_CLEAR' : 'SQUADRON_ALL' });
            }}
          />
        ) : null}
        <HudButton
          icon="undo"
          tone="float"
          label={t('game.action.undo')}
          keyHint="Z"
          disabled={!canUndo}
          why={t('game.action.undoNothing')}
          onClick={() => {
            send({ type: 'UNDO' });
          }}
        />
      </div>
      {phase === 'muster' && squadron === 0 ? (
        // U-MUST-03: with nobody chosen there is nothing to cast; the corner goes on to firing.
        <HudButton
          icon="skip"
          size="lg"
          tone="accent"
          label={t('game.action.skipSailing')}
          keyHint="Space"
          note={t('game.squadron.count', { n: 0, total: candidates })}
          badge={`0/${candidates}`}
          tip={false}
          testId="primary-action"
          onClick={() => {
            send({ type: 'SKIP_SAILING' });
          }}
        />
      ) : phase === 'muster' ? (
        <HudButton
          icon="wind"
          size="lg"
          tone="accent"
          label={t('game.action.cast', { n: squadron })}
          keyHint="Space"
          note={`${t('game.squadron.count', { n: squadron, total: candidates })} · ${t('game.action.noReturn')}`}
          badge={`${squadron}/${candidates}`}
          // The corner button speaks for itself: its count is on it, the rest is read out.
          tip={false}
          testId="primary-action"
          onClick={() => {
            send({ type: 'CAST' });
          }}
        />
      ) : (
        <HudButton
          icon="hourglass"
          size="lg"
          tone="accent"
          label={t('game.action.endTurn')}
          keyHint="Enter"
          // The corner button speaks for itself: its count is on it, the rest is read out.
          tip={false}
          testId="primary-action"
          onClick={() => {
            send({ type: 'END_TURN' });
          }}
        />
      )}
    </div>
  );
}

/**
 * U-SEL-02: the ships of a shared cell, each with its miniature, side and hull, next to the
 * cell. Picking one, a tap outside or Esc closes it.
 */
export function CellPicker({
  state,
  nations,
  ids,
  at,
  selected,
  squadron,
  onPick,
  onClose,
}: {
  state: GameState;
  nations: Readonly<Record<Side, string>>;
  ids: readonly string[];
  at: { x: number; y: number };
  selected: string | null;
  squadron?: PickSquadron | undefined;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: at.x + 16, top: at.y - 24 });
  useLayoutEffect(() => {
    const el = ref.current;
    const host = el?.offsetParent;
    if (!el || !(host instanceof HTMLElement)) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const [W, H] = [host.clientWidth, host.clientHeight];
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));
    // Beside the cell, right or left; where neither fits (a phone), below it or above it, so the
    // two ships stay in sight.
    if (at.x + 24 + w <= W - 12 || at.x - 24 - w >= 12) {
      const left = at.x + 24 + w <= W - 12 ? at.x + 24 : at.x - 24 - w;
      setPos({ left, top: clamp(at.y - h / 2, 64, H - h - 12) });
    } else {
      const top = at.y + 36 + h <= H - 96 ? at.y + 36 : at.y - 48 - h;
      setPos({ left: clamp(at.x - w / 2, 12, W - w - 12), top: clamp(top, 64, H - h - 12) });
    }
    el.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
  }, [at]);
  useEffect(() => {
    const away = (e: PointerEvent) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('pointerdown', away, true);
    return () => {
      window.removeEventListener('pointerdown', away, true);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      className={css.picker}
      style={pos}
      role="dialog"
      aria-label={t('game.sheet.picker')}
      data-testid="cell-picker"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <h2 className={css.pickerTitle}>{t('game.sheet.picker')}</h2>
      <PickList
        state={state}
        nations={nations}
        ids={ids}
        selected={selected}
        squadron={squadron}
        onPick={onPick}
      />
    </div>
  );
}

/**
 * While the squadron is chosen (U-MUST-01), picking a ship that may sail toggles it: its row
 * shows a check box's state.
 */
export interface PickSquadron {
  readonly chosen: ReadonlySet<string>;
  readonly candidates: ReadonlySet<string>;
}

/** The rows of the picker: large enough for a fingertip, so phones get the same popover. */
export function PickList({
  state,
  nations,
  ids,
  selected,
  squadron,
  onPick,
}: {
  state: GameState;
  nations: Readonly<Record<Side, string>>;
  ids: readonly string[];
  selected: string | null;
  squadron?: PickSquadron | undefined;
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '4px' }}>
      {ids.map((id) => {
        const ship = state.ships.find((s) => s.id === id);
        if (!ship) return null;
        const cls = state.ruleset.shipClasses[ship.cls];
        const enemy = ship.side !== state.turn.side;
        const toggles = squadron?.candidates.has(id) ?? false;
        const chosen = toggles && (squadron?.chosen.has(id) ?? false);
        return (
          <li key={id}>
            <button
              type="button"
              className={css.pickItem}
              data-current={selected === id}
              data-enemy={enemy}
              aria-pressed={toggles ? chosen : undefined}
              onClick={() => {
                onPick(id);
              }}
            >
              {toggles ? (
                <span className={css.pickCheck} aria-hidden="true">
                  {chosen ? <Icon name="check" /> : null}
                </span>
              ) : null}
              <ShipToken
                type={ship.cls}
                nation={nations[ship.side]}
                size={44}
                state={tokenStateOf(ship, cls)}
              />
              <span>
                <span className={css.pickName}>
                  <NationFlag nation={nations[ship.side]} height={12} decorative />
                  {unitLabel(t, state, nations, id)}
                </span>
                <span className={css.pickSub}>
                  <Icon name="hole" />
                  {ship.damage}/{cls.sinksAt}
                  <span className={css.srOnly}>
                    {t('game.unitPanel.holes', { n: ship.damage, max: cls.sinksAt })}
                  </span>
                  {' · '}
                  {state.players[ship.side].name}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export interface Toast {
  readonly key: string;
  readonly text: string;
  readonly tone?: 'bad';
  /** A toast that stays until dismissed has a button for it. */
  readonly onDismiss?: () => void;
}

/** Short notes over the board: the engine's refusal of the last try, why a cell is out of reach. */
export function Toasts({ toasts }: { toasts: readonly Toast[] }) {
  if (toasts.length === 0) return null;
  return (
    <div className={css.toasts}>
      {toasts.map((toast) => (
        <div
          key={toast.key}
          className={css.toast}
          data-tone={toast.tone}
          role={toast.tone === 'bad' ? 'alert' : 'status'}
        >
          <span style={{ flex: 1 }}>{toast.text}</span>
          {toast.onDismiss ? (
            <button type="button" className={css.toastButton} onClick={toast.onDismiss}>
              OK
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * A unit's tooltip on the board (U-SEL-01): its name, whose it is, its damage and, for an own
 * ship, whether it sails with the squadron this turn.
 */
export function UnitTipContent({ snapshot, id }: { snapshot: TurnSnapshot; id: string }) {
  const { t } = useTranslation();
  const state = current(snapshot.context.history);
  const nations = snapshot.context.history.nations;
  const ship = state.ships.find((s) => s.id === id);
  const fort = ship ? undefined : state.forts.find((f) => f.id === id);
  const unit = ship ?? fort;
  if (!unit) return null;
  const cls = ship ? state.ruleset.shipClasses[ship.cls] : fort ? state.ruleset.fortClasses[fort.cls] : null;
  if (!cls) return null;
  // A ship's holes or a fort's hits, against what sinks or destroys it.
  const hurt = { n: ship?.damage ?? fort?.hits ?? 0, max: 'sinksAt' in cls ? cls.sinksAt : cls.destroyedAt };
  // Holes, shots left this turn and range, as icons: the words are for screen readers.
  const stats: { icon: IconName; text: string; label: string; bad?: boolean }[] = [
    {
      icon: 'hole',
      text: `${hurt.n}/${hurt.max}`,
      label: ship ? t('game.unitPanel.holes', hurt) : t('game.unitPanel.hits', hurt),
      bad: hurt.n > 0,
    },
    {
      icon: 'cannon',
      text: `${unit.shotsLeft}/${cls.shotsPerTurn}`,
      label: t('game.inspector.shotsValue', { n: unit.shotsLeft, max: cls.shotsPerTurn }),
    },
    { icon: 'range', text: String(cls.range), label: t('game.inspector.cells', { n: cls.range }) },
  ];
  let sails: string | null = null;
  if (ship && ship.side === state.turn.side && !ship.sunk) {
    if (uiPhase(snapshot) === 'muster') {
      sails = liveSquadron(snapshot.context).includes(id)
        ? t('game.squadron.in')
        : squadronCandidates(state).includes(id)
          ? t('game.squadron.out')
          : t('game.squadron.cannot');
    } else if (state.turn.spin) sails = ship.inGroup ? t('game.squadron.in') : t('game.squadron.out');
  }
  const repair =
    ship?.dockedOnTurn != null && !ship.sunk
      ? t('game.inspector.repairDock', { n: dockTurnsLeft(ship, state.turn.number) })
      : fort?.repair
        ? t('game.inspector.repairFort', {
            n: fortRepairTurnsLeft(fort, state.ruleset.fortRepairPerTurn, state.turn.number),
          })
        : null;
  const hold = ship ? royalHold(ship, state) : null;
  const phase = uiPhase(snapshot);
  const shooters =
    !snapshot.context.replay && (phase === 'muster' || phase === 'battle') ? attackersOf(state, id) : [];
  const shots = shooters.reduce((n, s) => n + shotsLeft(state, s), 0);
  return (
    <>
      <span className={css.tooltipLabel}>
        <NationFlag nation={nations[unit.side]} height={11} decorative />
        {unitLabel(t, state, nations, id)}
      </span>
      <span className={css.tooltipStats}>
        {stats.map((s) => (
          <span key={s.icon} className={css.tooltipStat} data-bad={s.bad}>
            <Icon name={s.icon} size={13} />
            <span aria-hidden="true">{s.text}</span>
            <span className={css.srOnly}>{s.label}</span>
          </span>
        ))}
      </span>
      {sails ? <span data-sails={sails === t('game.squadron.in')}>{sails}</span> : null}
      {repair ? <span data-repair>{repair}</span> : null}
      {hold ? (
        <span className={css.tooltipRoyal} data-royal>
          {t('game.unitPanel.royalHold', { n: hold.held, max: hold.needed, left: hold.needed - hold.held })}
        </span>
      ) : null}
      {shooters.length ? (
        <span className={css.tooltipWhy} data-threat>
          {t('game.fire.inSights', { n: shooters.length, shots })}
        </span>
      ) : null}
    </>
  );
}
